import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-0 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline:
          "border border-input bg-transparent hover:bg-accent hover:text-accent-foreground",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
        gradient:"bg-gradient-to-br from-[#478EC0] to-[#4EA983] text-white",
        ternaryActive: "border border-[#999999] bg-[#478EC0]/[0.2] text-[#444444] hover:bg-[#478EC0]/[0.1]",  //ternary active button
        gradientBorder: "text-[#444444] border border-[#478EC0] hover:bg-[#EDF4F9] ", //secondary border button
        text: "h-auto p-0 min-h-[28px] min-w-0 bg-transparent border-none text-[#478EC0] hover:text-[#3E7FAE] hover:bg-[#edf4f9] focus-visible:ring-0 focus-visible:ring-offset-0", // for only text buttons
        tertiaryBtn: "text-[#444444] border border-[#DAE8F2] hover:bg-[#EDF4F9]", // tertiary button
        Error: "bg-[#fee6e6] text-[#444444] border border-[#f50000] hover:bg-[#fdcccc]", // Error Button
      },
   
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
